import PriceFormat from '@website/components/utilities/PriceFormat';
import { Currency } from '@website/types/graphqlOperations';
import ExceptionalCircumstances from './ExceptionalCircumstances';
import { TravelInsurance } from './TravelInsurance';

/**
 * Custom message to show if the deposit is non-refundable
 */
export function DepositNonRefundableMessage() {
  return (
    <>
      <p>
        Your deposit for this trip is unfortunately non-refundable, as it is
        used to purchase non-refundable permits.
      </p>

      <p>
        Please do continue to read below, and then we do still request that you
        cancel the spaces if you are not able to attend. This is so the guides
        know you are not coming.
      </p>

      <ExceptionalCircumstances />

      <TravelInsurance />
    </>
  );
}

/**
 * Balance Refund Cancellation Description
 */
export default function BalanceRefundCancel({
  guestRefund,
  guestCredits,
  currency,
}: {
  guestRefund: number;
  guestCredits: number;
  currency: Currency;
}) {
  // If there is no credit available, it must be a non-refundable deposit trip
  if (!guestCredits && !guestRefund) {
    if (!guestRefund) {
      return (
        <div>
          <h6>
            Unfortunately it's no longer possible to get a refund or change
            dates
          </h6>

          <DepositNonRefundableMessage />
        </div>
      );
    }

    // If there a refund, the balance has been paid as well
    return (
      <div>
        <h6>
          You'll get a partial refund of{' '}
          <PriceFormat amount={guestRefund} currencyIso={currency} />
        </h6>

        <DepositNonRefundableMessage />
      </div>
    );
  }

  // Otherwise show the standard lifetime credit options
  return (
    <div>
      <h6>
        You'll get a credit of{' '}
        <PriceFormat amount={guestCredits} currencyIso={currency} />
        {guestRefund > 0 && (
          <>
            {' '}
            and a refund of{' '}
            <PriceFormat amount={guestRefund} currencyIso={currency} />
          </>
        )}
      </h6>

      <p>
        Great news - as you are more than 2 months before the start of the trip,
        you're covered under our lifetime deposit policy.
      </p>

      <p>The way this works is as follows:</p>

      <ul>
        <li>
          Once you've cancelled the spaces below, this will result in a credit
          of <PriceFormat amount={guestCredits} currencyIso={currency} /> being
          applied to your Skyhook account.
        </li>

        <li>
          You can use this credit to book a new date, or a different trip. It
          can be applied to the deposit of any trip, but please note it cannot
          be applied to the balance payment.
        </li>

        {guestRefund > 0 && (
          <li>
            You'll also get a refund of{' '}
            <PriceFormat amount={guestRefund} currencyIso={currency} />. This is
            because we can't move the balance payment across automatically, so
            instead we refund that part to you.
          </li>
        )}

        <li>
          There will be instructions for how to use the credit on your
          dashboard.
        </li>
      </ul>
    </div>
  );
}
